import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // 1. Check local storage (JWT fallback)
        const token = localStorage.getItem('token');
        if (token) {
           const userData = localStorage.getItem('user');
           if (userData) setUser(JSON.parse(userData));
        }

        // 2. Verify with backend (Cookie/Session check)
        // This is important for Google Auth or invalidating stale JWTs
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch (error) {
        // If 401, clear local state
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);
      
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, formData);
      const { access_token, user_id, role, name } = response.data;
      
      localStorage.setItem('token', access_token);
      const userData = { id: user_id, role, name, email };
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return true;
    } catch (error) {
      console.error("Login failed", error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, userData);
       const { access_token, user_id, role, name } = response.data;
      
      localStorage.setItem('token', access_token);
      const userObj = { id: user_id, role, name, email: userData.email };
      localStorage.setItem('user', JSON.stringify(userObj));
      setUser(userObj);
      return true;
    } catch (error) {
      console.error("Registration failed", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    // Optional: Call backend logout to clear cookie
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
